// weatherStyle.ts
// ─────────────────────────────────────────────────────────────────────────────
// Faint coastline stroke over ALL water polygons (ocean + inland). We don't
// filter inland water out: the Protomaps schema tags small ponds with the same
// `kind`/empty props as the ocean, so no filter reliably separates them. A
// thin, semi-transparent line keeps the ocean coast legible while pond
// outlines recede.
//
// FLAVOR-AWARE: a white line at 0.8 looks right on light basemaps but glares
// on dark/black ones. addWeatherCoastline takes the current flavor and picks a
// color + opacity that suits it. Pass the same `flavor` string you hand to
// buildDarkStyle.

const COASTLINE_LAYER_ID = "weather-coastline";

// Per-flavor coastline appearance. Light bases get a white-ish line; dark
// bases get a softer, lower-opacity line so it reads as a subtle edge rather
// than a bright glare. "grayscale" treated as a light-ish base.
function coastlineStyleFor(flavor: string): { color: string; opacity: number } {
  switch (flavor) {
    case "dark":
      return { color: "#9fb2c4", opacity: 0.35 };
    case "black":
      return { color: "#8aa0b4", opacity: 0.30 };
    case "light":
    case "white":
      return { color: "#ffffff", opacity: 0.8 };
    case "grayscale":
      return { color: "#ffffff", opacity: 0.6 };
    default:
      // Unknown flavor — moderate neutral that won't glare on either extreme.
      return { color: "#cdd6df", opacity: 0.5 };
  }
}

export function addWeatherCoastline(map: any, beforeId?: string, flavor: string = "dark") {
  if (map.getLayer && map.getLayer(COASTLINE_LAYER_ID)) {
    map.removeLayer(COASTLINE_LAYER_ID);
  }

  const { color, opacity } = coastlineStyleFor(flavor);

  map.addLayer({
    id: COASTLINE_LAYER_ID,
    type: "line",
    source: "protomaps",
    "source-layer": "water",
    paint: {
      "line-color": color,
      "line-width": ["interpolate", ["linear"], ["zoom"],
        3, 0.4,
        6, 1.0,
        9, 2.0,
      ],
      "line-opacity": opacity,
    },
  } as any, beforeId);
}

// Update color/opacity of an already-added coastline without removing it —
// handy on a flavor change if the layer is still present. Falls back to a
// no-op if the layer isn't there yet.
export function restyleWeatherCoastline(map: any, flavor: string) {
  if (!map.getLayer || !map.getLayer(COASTLINE_LAYER_ID)) return;
  const { color, opacity } = coastlineStyleFor(flavor);
  map.setPaintProperty(COASTLINE_LAYER_ID, "line-color", color);
  map.setPaintProperty(COASTLINE_LAYER_ID, "line-opacity", opacity);
}

export function removeWeatherCoastline(map: any) {
  if (map.getLayer && map.getLayer(COASTLINE_LAYER_ID)) {
    map.removeLayer(COASTLINE_LAYER_ID);
  }
}

export { COASTLINE_LAYER_ID };

// ─────────────────────────────────────────────────────────────────────────────
// Feature-layer visibility toggles (roads / highways / landuse / buildings)
// ─────────────────────────────────────────────────────────────────────────────
// The settings panel exposes four on/off toggles that show or hide groups of
// Protomaps basemap layers. We classify layers by ID *pattern* (not a hardcoded
// list) so the grouping survives Protomaps adding new sublayers. Each toggle
// maps to one FeatureGroup; classifyFeatureLayer returns which group a layer
// belongs to (or null if it isn't user-toggleable — e.g. water, boundaries,
// place labels, the weather coastline).

export type FeatureGroup = "roads" | "highways" | "landuse" | "buildings";

/**
 * Which toggle group a Protomaps layer ID belongs to, or null if it should
 * always stay visible (not user-toggleable).
 *
 * Highways are split out from roads: any "highway" or "major" road layer
 * (including its casing / tunnel / bridge variants and the major road labels &
 * shields) is a HIGHWAY; the remaining drivable street layers (minor, link,
 * service, other, plus their labels and oneway arrows) are ROADS. Rail, piers,
 * runways and taxiways are left always-on (they aren't "roads" in the everyday
 * sense and there's no toggle for them).
 */
export function classifyFeatureLayer(id: string): FeatureGroup | null {
  if (!id) return null;

  // Buildings — single layer.
  if (id === "buildings") return "buildings";

  // Landuse / landcover fills (parks, urban green, industrial, etc.).
  // NOTE: deliberately excludes "earth" (the base landmass) and "water".
  if (id === "landcover" || id.startsWith("landuse")) return "landuse";

  // Road family. Split highways vs. ordinary roads.
  // Match the road-ish symbol layers too (labels, shields, oneway arrows).
  const isRoadLine = id.startsWith("roads_");
  const isRoadSymbol = id === "roads_oneway" || id === "roads_shields"
    || id === "roads_labels_minor" || id === "roads_labels_major";
  if (isRoadLine || isRoadSymbol) {
    // Always-on road-adjacent infrastructure — not part of either toggle.
    if (id.includes("rail") || id.includes("pier")
      || id.includes("runway") || id.includes("taxiway")) return null;

    // Highways / major arterials (and their casings, tunnels, bridges, labels).
    if (id.includes("highway") || id.includes("major")) return "highways";

    // Everything else road-ish is an ordinary road.
    return "roads";
  }

  return null;
}

export interface FeatureToggles {
  roads: boolean;
  highways: boolean;
  landuse: boolean;
  buildings: boolean;
}

/**
 * Apply the four feature toggles to the LIVE map in place, via
 * setLayoutProperty("visibility", ...). No setStyle — instant and leaves the
 * custom weather layers untouched. Safe to call repeatedly and mid-restyle
 * (missing layers are skipped).
 */
export function applyFeatureVisibility(map: any, toggles: FeatureToggles): void {
  if (!map || !map.getStyle) return;
  let style: any;
  try { style = map.getStyle(); } catch { return; }
  const layers = style?.layers ?? [];
  for (const lyr of layers) {
    const group = classifyFeatureLayer(lyr?.id ?? "");
    if (!group) continue;
    const visible = toggles[group];
    try {
      map.setLayoutProperty(lyr.id, "visibility", visible ? "visible" : "none");
    } catch { /* layer may be missing mid-restyle; ignore */ }
  }
}

/**
 * Bake the feature toggles into a freshly-built layer list (used inside
 * buildDarkStyle) so a basemap swap comes back with the user's current
 * show/hide choices already applied — no post-swap flicker of hidden layers.
 */
export function bakeFeatureVisibility(styleLayers: any[], toggles: FeatureToggles): any[] {
  return styleLayers.map((lyr) => {
    const group = classifyFeatureLayer(lyr?.id ?? "");
    if (!group) return lyr;
    const visible = toggles[group];
    return {
      ...lyr,
      layout: { ...(lyr.layout ?? {}), visibility: visible ? "visible" : "none" },
    };
  });
}